<template>
  <Modal
    :is-open="isOpen"
    icon="barcode"
    title="Decode Vehicle"
    description="Check the decoded vehicle before saving it."
    :buttons="['close', 'ok']"
    close-text="Cancel"
    ok-text="Save vehicle"
    min-width="520"
    max-width="720"
    :is-confirming="isSaving"
    :ok-is-disabled="!canSave"
    :disable-close="isSaving"
    @close-modal="$emit('close')"
    @ok-modal="$emit('save')"
  >
    <div
      v-if="isDecoding"
      class="flex flex-col items-center justify-center py-10"
    >
      <Icon
        type="spinner-third"
        class="fa-spin text-3xl text-secondary"
      />
      <div class="mt-3 text-sm text-quaternary">Decoding vehicle</div>
    </div>

    <div v-else>
      <div
        v-if="vehicleString"
        class="mb-4 rounded-lg bg-tertiary px-4 py-3 text-sm text-secondary"
      >
        {{ vehicleString }}
      </div>

      <div
        v-if="decodeError"
        class="mb-4 text-sm text-error"
      >
        {{ decodeError }}
      </div>

      <div
        v-if="missingRequiredFields.length > 0 && decodedVehicle"
        class="mb-4 text-sm text-error"
      >
        {{ requiredSaveMessage }}
      </div>

      <div
        v-if="!hasVin && decodedVehicle"
        class="mb-4 text-sm text-quaternary"
      >
        This agreement has no VIN, so the vehicle cannot be saved yet.
      </div>

      <div
        v-if="decodedVehicle"
        class="grid grid-cols-1 gap-3 sm:grid-cols-2"
      >
        <div
          v-for="field in decodedVehicleFields"
          :key="field.key"
          class="rounded-md px-2 py-1"
          :class="fieldClass(field.key, field.required)"
        >
          <div
            class="text-xs"
            :class="isMissingField(field.key) ? 'text-error' : 'text-quaternary'"
          >
            {{ field.label }}
            <span v-if="field.required">*</span>
          </div>
          <div
            class="text-sm font-medium"
            :class="isMissingField(field.key) ? 'text-error' : 'text-secondary'"
          >
            {{ formatDecodedVehicleValue(decodedVehicle[field.key]) }}
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
// composables
import { computed } from 'vue'
// components
import Modal from '@/components/modal/Modal.vue'
import Icon from '@/components/icon/Icon.vue'
// utilities
import {
  decodedVehicleFields,
  formatDecodedVehicleValue,
  formatMissingDecodedFields
} from '@/utilities/vehicle/decodeVehicle'
// types
import type { DecodedVehicle } from '@/types/domain/customerVehicle'
import type { RequiredDecodedVehicleField } from '@/utilities/vehicle/decodeVehicle'

interface Props {
  isOpen: boolean
  isDecoding: boolean
  isSaving: boolean
  canSave: boolean
  hasVin: boolean
  vehicleString: string
  decodedVehicle: DecodedVehicle | null
  missingFields: string[]
  missingRequiredFields: RequiredDecodedVehicleField[]
  decodeError: string | null
}

const props = defineProps<Props>()

/**
 * Names only the save fields that are still empty.
 */
const requiredSaveMessage = computed(() => {
  const labels = formatMissingDecodedFields(props.missingRequiredFields)
  const verb = props.missingRequiredFields.length === 1 ? 'is' : 'are'
  return `${labels} ${verb} required before this vehicle can be saved.`
})

/**
 * Whether a required decoded field is empty.
 */
function isMissingRequired(key: keyof DecodedVehicle): boolean {
  return props.missingRequiredFields.includes(key as RequiredDecodedVehicleField)
}

/**
 * Whether the decoder or the save rules marked this field as missing.
 */
function isMissingField(key: keyof DecodedVehicle): boolean {
  return isMissingRequired(key) || props.missingFields.includes(key)
}

/**
 * Highlights required fields, and uses the error colour when one is empty.
 */
function fieldClass(key: keyof DecodedVehicle, required?: boolean): string {
  if (isMissingField(key)) {
    return 'border border-error bg-error-secondary'
  }

  if (required) {
    return 'border border-secondary'
  }

  return ''
}

defineEmits<{
  close: []
  save: []
}>()
</script>
